// Brand color palette — dark navy + gold institutional luxury aesthetic
// Spec: docs/superpowers/specs/2026-05-03-blp-landing-redesign-design.md §2.1
//
// This module preserves legacy exports (`semanticColors`, `cssCustomProperties`,
// `getColor`) as backward-compatibility shims so non-rewritten components
// (CookieConsentBanner, RunningBanner) keep compiling. Their values are
// remapped onto the new navy + gold palette.

// ---------- Primary palette ----------
const navy = {
  900: '#071326', // page base / hero base
  800: '#0A1B34', // elevated sections (stats, testimonials, properties)
  700: '#0E2242', // cards, footer top zone
  600: '#101826', // secondary surfaces
  500: '#131F31', // hover surfaces
};

const gold = {
  600: '#C99846', // pressed / active
  500: '#D4A85F', // primary gold (default)
  400: '#E0B873', // gradient end / hover
};

const text = {
  primary: '#F5F7FA',
  secondary: '#D6DCE5',
  muted: '#9AA7B8',
};

const border = {
  subtle: 'rgba(255,255,255,0.08)',
  hover: 'rgba(212,168,95,0.20)',
};

// ---------- brandColors (compat shape used across the codebase) ----------
export const brandColors = {
  // New canonical keys
  navy,
  gold,
  text,
  border,

  // Legacy keys (mapped to new palette)
  primary: gold[500],
  primaryHover: gold[600],
  primaryLight: 'rgba(212,168,95,0.10)',
  primaryDark: gold[600],
  secondary: gold[500],
  secondaryHover: gold[600],
  secondaryLight: 'rgba(212,168,95,0.08)',
  secondaryDark: gold[600],
  tertiary: gold[400],
  tertiaryHover: gold[500],
  tertiaryLight: 'rgba(212,168,95,0.06)',
  tertiaryDark: gold[600],
  gradientStart: gold[500],
  gradientMid1: gold[400],
  gradientMid2: gold[400],
  gradientMid3: gold[400],
  gradientEnd: gold[400],
  gradientStartDark: gold[500],
  gradientMid1Dark: gold[400],
  gradientMid2Dark: gold[400],
  gradientMid3Dark: gold[400],
  gradientEndDark: gold[400],
  backgroundLight: 'rgba(212,168,95,0.05)',
  backgroundMedium: 'rgba(212,168,95,0.10)',
  backgroundStrong: 'rgba(212,168,95,0.20)',
  backgroundLightDark: 'rgba(212,168,95,0.08)',
  backgroundMediumDark: 'rgba(212,168,95,0.15)',
  backgroundStrongDark: 'rgba(212,168,95,0.25)',
  shadowLight: 'rgba(212,168,95,0.20)',
  shadowMedium: 'rgba(212,168,95,0.30)',
  shadowStrong: 'rgba(212,168,95,0.40)',
  shadowLightDark: 'rgba(212,168,95,0.30)',
  shadowMediumDark: 'rgba(212,168,95,0.40)',
  shadowStrongDark: 'rgba(212,168,95,0.50)',
};

// ---------- semanticColors (extended; old keys preserved) ----------
export const semanticColors = {
  // New canonical
  surface: {
    base: navy[900],
    elevated: navy[800],
    card: navy[700],
  },
  text,
  border,

  // Legacy buckets — remapped onto navy + gold
  button: {
    primary: gold[500],
    primaryHover: gold[600],
    secondary: gold[400],
    secondaryHover: gold[500],
  },
  link: {
    default: gold[500],
    hover: gold[400],
    visited: gold[600],
  },
  badge: {
    background: gold[500],
    backgroundLight: 'rgba(212,168,95,0.15)',
    text: navy[900],
    textDark: gold[500],
  },
  background: {
    light: 'rgba(212,168,95,0.05)',
    medium: 'rgba(212,168,95,0.10)',
    strong: 'rgba(212,168,95,0.20)',
    gradient: 'linear-gradient(135deg, #D4A85F 0%, #E0B873 100%)',
    gradientFull: 'linear-gradient(135deg, #D4A85F 0%, #E0B873 100%)',
    surfaceGradient: 'linear-gradient(135deg, #071326 0%, #0E2242 100%)',
    goldGradient: 'linear-gradient(135deg, #D4A85F 0%, #E0B873 100%)',
    lightDark: 'rgba(212,168,95,0.08)',
    mediumDark: 'rgba(212,168,95,0.15)',
    strongDark: 'rgba(212,168,95,0.25)',
    gradientDark: 'linear-gradient(135deg, #D4A85F 0%, #E0B873 100%)',
    gradientFullDark: 'linear-gradient(135deg, #D4A85F 0%, #E0B873 100%)',
  },
  shadow: {
    light: '0 4px 12px rgba(212,168,95,0.20)',
    medium: '0 4px 12px rgba(212,168,95,0.30)',
    strong: '0 4px 12px rgba(212,168,95,0.40)',
    lightDark: '0 4px 12px rgba(212,168,95,0.30)',
    mediumDark: '0 4px 12px rgba(212,168,95,0.40)',
    strongDark: '0 4px 12px rgba(212,168,95,0.50)',
  },
  scrollbar: {
    thumb: gold[500],
    thumbHover: gold[400],
    track: navy[900],
    trackDark: navy[900],
  },
};

// ---------- cssCustomProperties (CSS file consumers) ----------
export const cssCustomProperties = {
  '--brand-primary': gold[500],
  '--brand-primary-hover': gold[600],
  '--brand-primary-light': 'rgba(212,168,95,0.10)',
  '--brand-secondary': gold[500],
  '--brand-secondary-hover': gold[600],
  '--brand-tertiary': gold[400],
  '--brand-tertiary-hover': gold[500],
  '--brand-background-light': 'rgba(212,168,95,0.05)',
  '--brand-background-medium': 'rgba(212,168,95,0.10)',
  '--brand-shadow-light': 'rgba(212,168,95,0.20)',
  '--brand-shadow-medium': 'rgba(212,168,95,0.30)',
  '--scrollbar-thumb': gold[500],
  '--scrollbar-thumb-hover': gold[400],
  '--surface-base': navy[900],
  '--surface-elevated': navy[800],
  '--surface-card': navy[700],
  '--text-primary': text.primary,
  '--text-secondary': text.secondary,
  '--text-muted': text.muted,
  '--border-subtle': border.subtle,
};

// ---------- Chakra color scales (driven into theme/index.js) ----------
export const chakraColorScales = {
  brand: {
    50: '#F8EFD8',
    100: '#F1DFAE',
    200: '#E8CC83',
    300: '#E0B873',
    400: '#D4A85F',
    500: '#D4A85F',
    600: '#C99846',
    700: '#A57A38',
    800: '#7F5E2A',
    900: '#594120',
  },
  primary: {
    50: navy[700],
    100: navy[700],
    200: navy[700],
    300: navy[800],
    400: navy[800],
    500: navy[900],
    600: navy[900],
    700: navy[900],
    800: navy[900],
    900: navy[900],
  },
  secondary: {
    50: navy[700],
    100: navy[700],
    200: navy[800],
    300: navy[800],
    400: navy[800],
    500: navy[900],
    600: navy[900],
    700: navy[900],
    800: navy[900],
    900: navy[900],
  },
  accent: {
    50: '#F8EFD8',
    100: '#F1DFAE',
    200: '#E8CC83',
    300: '#E0B873',
    400: '#D4A85F',
    500: '#D4A85F',
    600: '#C99846',
    700: '#A57A38',
    800: '#7F5E2A',
    900: '#594120',
  },
  navy,
  gold,
  darkGray: {
    50: '#F5F7FA',
    100: '#D6DCE5',
    200: '#9AA7B8',
    300: '#6B7B8F',
    400: '#4A5A6E',
    500: '#2F3D52',
    600: '#1F2A3D',
    700: '#131F31',
    800: '#0E2242',
    900: '#071326',
  },
  gray: {
    50: '#F5F7FA',
    100: '#D6DCE5',
    200: '#9AA7B8',
    300: '#6B7B8F',
    400: '#4A5A6E',
    500: '#2F3D52',
    600: '#1F2A3D',
    700: '#131F31',
    800: '#0E2242',
    900: '#071326',
  },
  success: { 500: '#34c759' },
  warning: { 500: '#D4A85F' },
  danger: { 500: '#ff3b30' },
};

// ---------- getColor utility (legacy) ----------
export const getColor = (path) => {
  const segments = path.split('.');
  let current = semanticColors;
  for (const segment of segments) {
    if (current && current[segment] !== undefined) {
      current = current[segment];
    } else {
      // Fallback to gold instead of failing
      return gold[500];
    }
  }
  return current;
};

const colorSystem = {
  brandColors,
  semanticColors,
  cssCustomProperties,
  chakraColorScales,
  getColor,
};

export default colorSystem;
